import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "./shared";

const TYPES = [
  { k: "percent", l: "Percentage off", hint: "e.g. 10% off, or 100% for free photos" },
  { k: "buy_x_get_y", l: "Buy X get Y free", hint: "e.g. 3 for 2 — cheapest photos are free" },
  { k: "bundle", l: "Bundle price", hint: "e.g. 10 photos for £10" },
  { k: "fixed", l: "Fixed amount off", hint: "e.g. £5 off the basket" },
];

const EMPTY = { code: "", type: "percent", percent: "", amount: "", buy_qty: "2", free_qty: "1", bundle_qty: "10", bundle_price: "", currency: "gbp", event_id: "", min_items: "", max_uses: "", expires_at: "", photographer_id: "" };

export default function DiscountsPanel({ events, isSuper }) {
  const [codes, setCodes] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/discounts");
      setCodes(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    if (isSuper) api.get("/admin/photographers").then((r) => setPhotographers(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (v) => (v === "" ? null : Number(v));

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        code: form.code, type: form.type, currency: form.currency,
        percent: num(form.percent), amount: num(form.amount),
        buy_qty: num(form.buy_qty), free_qty: num(form.free_qty),
        bundle_qty: num(form.bundle_qty), bundle_price: num(form.bundle_price),
        event_id: form.event_id || null, min_items: num(form.min_items), max_uses: num(form.max_uses),
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null,
        photographer_id: form.photographer_id || null,
      };
      const r = await api.post("/admin/discounts", payload);
      toast.success(`Code ${r.data.code} created — ${r.data.description}`);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (c) => {
    try {
      await api.put(`/admin/discounts/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const feature = async (c) => {
    try {
      await api.put(`/admin/discounts/${c.id}`, { featured: !c.featured });
      toast.success(c.featured ? "Code hidden from highlights page and receipts" : "Code now shown on your highlights page and in buyers' receipts");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? Buyers won't be able to use it any more.`)) return;
    try {
      await api.delete(`/admin/discounts/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-10" data-testid="discounts-panel">
      <div className="border border-white/10 bg-[#0a0a0a] p-5">
        <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">New discount code</p>
        <p className="text-xs text-white/50 mt-1 font-condensed">
          Codes only discount {isSuper ? "the chosen photographer's" : "your own"} photos in a buyer's basket. Your payout and commission are both worked out on the discounted price.
          Mark a code <span className="pm-orange">★ Featured</span> to show it on your public highlights page and in buyers' receipt emails.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          <Field label="Code">
            <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="TRACKDAY10" className="input-minimal text-sm py-2 uppercase" data-testid="discount-code-input" />
          </Field>
          <Field label="Type">
            <select value={form.type} onChange={set("type")} className="input-minimal bg-transparent text-sm py-2" data-testid="discount-type-select">
              {TYPES.map((t) => <option key={t.k} value={t.k} className="bg-black">{t.l}</option>)}
            </select>
            <span className="text-[10px] text-white/40 font-condensed">{TYPES.find((t) => t.k === form.type)?.hint}</span>
          </Field>
          {form.type === "percent" && (
            <Field label="Percent off">
              <input type="number" min="1" max="100" value={form.percent} onChange={set("percent")} placeholder="10" className="input-minimal text-sm py-2" data-testid="discount-percent-input" />
            </Field>
          )}
          {form.type === "fixed" && (
            <Field label="Amount off">
              <input type="number" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} placeholder="5.00" className="input-minimal text-sm py-2" data-testid="discount-amount-input" />
            </Field>
          )}
          {form.type === "buy_x_get_y" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Buy">
                <input type="number" min="1" value={form.buy_qty} onChange={set("buy_qty")} className="input-minimal text-sm py-2" data-testid="discount-buy-input" />
              </Field>
              <Field label="Get free">
                <input type="number" min="1" value={form.free_qty} onChange={set("free_qty")} className="input-minimal text-sm py-2" data-testid="discount-free-input" />
              </Field>
            </div>
          )}
          {form.type === "bundle" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Photos">
                <input type="number" min="2" value={form.bundle_qty} onChange={set("bundle_qty")} className="input-minimal text-sm py-2" data-testid="discount-bundle-qty-input" />
              </Field>
              <Field label="Bundle price">
                <input type="number" min="0" step="0.01" value={form.bundle_price} onChange={set("bundle_price")} placeholder="10.00" className="input-minimal text-sm py-2" data-testid="discount-bundle-price-input" />
              </Field>
            </div>
          )}
          {(form.type === "fixed" || form.type === "bundle") && (
            <Field label="Currency">
              <select value={form.currency} onChange={set("currency")} className="input-minimal bg-transparent text-sm py-2" data-testid="discount-currency-select">
                {["gbp", "eur", "usd"].map((c) => <option key={c} value={c} className="bg-black">{c.toUpperCase()}</option>)}
              </select>
            </Field>
          )}
          {isSuper && (
            <Field label="Photographer">
              <select value={form.photographer_id} onChange={set("photographer_id")} className="input-minimal bg-transparent text-sm py-2" data-testid="discount-photographer-select">
                <option value="" className="bg-black">My own photos (admin account)</option>
                {photographers.map((p) => <option key={p.id} value={p.id} className="bg-black">{p.company_name || p.name} · {p.email}</option>)}
              </select>
            </Field>
          )}
          <Field label="Only for event (optional)">
            <select value={form.event_id} onChange={set("event_id")} className="input-minimal bg-transparent text-sm py-2" data-testid="discount-event-select">
              <option value="" className="bg-black">Any event</option>
              {events.map((ev) => <option key={ev.id} value={ev.id} className="bg-black">{ev.date} · {ev.track} · {ev.name}</option>)}
            </select>
          </Field>
          <Field label="Min eligible photos (optional)">
            <input type="number" min="1" value={form.min_items} onChange={set("min_items")} placeholder="—" className="input-minimal text-sm py-2" data-testid="discount-min-items-input" />
          </Field>
          <Field label="Max uses (optional)">
            <input type="number" min="1" value={form.max_uses} onChange={set("max_uses")} placeholder="Unlimited" className="input-minimal text-sm py-2" data-testid="discount-max-uses-input" />
          </Field>
          <Field label="Expires on (optional)">
            <input type="date" value={form.expires_at} onChange={set("expires_at")} className="input-minimal text-sm py-2" data-testid="discount-expires-input" />
          </Field>
        </div>
        <button onClick={create} disabled={busy || form.code.length < 2} className="pm-btn mt-6 disabled:opacity-40" data-testid="discount-create-button">
          {busy ? "Creating…" : "Create code"}
        </button>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-[0.3em] font-semibold mb-4 pb-3 border-b-2 text-white/60 border-white/10">Your codes ({codes.length})</h3>
        {codes.length === 0 ? (
          <p className="text-white/50 text-sm" data-testid="discounts-empty">No discount codes yet.</p>
        ) : (
          <div className="space-y-2" data-testid="discounts-list">
            {codes.map((c) => <CodeRow key={c.id} c={c} events={events} isSuper={isSuper} onToggle={toggle} onFeature={feature} onDelete={remove} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CodeRow({ c, events, isSuper, onToggle, onFeature, onDelete }) {
  const ev = events.find((e) => e.id === c.event_id);
  const expired = c.expires_at && c.expires_at < new Date().toISOString();
  const exhausted = c.max_uses && c.uses >= c.max_uses;
  const limits = [
    ev && `${ev.track} only`,
    c.min_items && `min ${c.min_items} photos`,
    c.max_uses && `${c.uses}/${c.max_uses} uses`,
    c.expires_at && `${expired ? "expired" : "until"} ${new Date(c.expires_at).toLocaleDateString("en-GB")}`,
  ].filter(Boolean).join(" · ");
  return (
    <div className={`flex flex-wrap items-center gap-4 border-2 border-white/10 bg-[#0a0a0a] p-4 ${!c.active || expired || exhausted ? "opacity-60" : ""}`} data-testid={`discount-row-${c.code}`}>
      <div className="min-w-[160px]">
        <p className="font-display text-2xl uppercase tracking-wider pm-orange">{c.code}</p>
        <p className="text-[10px] uppercase tracking-[0.15em] text-white/40">{isSuper ? c.photographer_name : ""}</p>
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-condensed">{c.description}</p>
        <p className="text-xs text-white/50 font-condensed">{limits || "No limits"}</p>
      </div>
      <div className="text-xs text-white/60 font-condensed text-right">
        <p data-testid={`discount-uses-${c.code}`}>{c.uses || 0} use{c.uses === 1 ? "" : "s"}</p>
        <p>{formatPrice(c.total_discounted || 0, c.currency)} given</p>
      </div>
      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 font-semibold ${c.active && !expired && !exhausted ? "bg-[var(--pm-orange)]/20 pm-orange" : "bg-white/10 text-white/50"}`} data-testid={`discount-status-${c.code}`}>
        {!c.active ? "Paused" : expired ? "Expired" : exhausted ? "Used up" : "Live"}
      </span>
      <button
        onClick={() => onFeature(c)}
        title="Featured codes appear on your public highlights page and in buyers' receipt emails"
        className={`text-xs uppercase tracking-[0.2em] ${c.featured ? "pm-orange" : "text-white/50 hover:text-white"}`}
        data-testid={`discount-feature-${c.code}`}
      >
        {c.featured ? "★ Featured" : "☆ Feature"}
      </button>
      <button onClick={() => onToggle(c)} className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[var(--pm-orange)]" data-testid={`discount-toggle-${c.code}`}>
        {c.active ? "Pause" : "Resume"}
      </button>
      <button onClick={() => onDelete(c)} className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300" data-testid={`discount-delete-${c.code}`}>Delete</button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</span>
      {children}
    </label>
  );
}
